import { gql, useMutation } from '@apollo/client';
import { Box, Typography, Card, Stack, Button } from '@mui/joy';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate } from 'react-router';
import { useFeedback } from '../feedback/Service';
import { parseGraphQLError } from '../../utils';
import useUser from './useUser';

const UPDATE_ATS_DECISION = gql`
  mutation UpdateATSDecision($usesATS: Boolean!) {
    updateATSDecision(usesATS: $usesATS) {
      _id
      usesATS
      selectedATSProvider
    }
  }
`;

const ATSDecision = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { user } = useUser();

  const [updateDecision, { loading }] = useMutation(UPDATE_ATS_DECISION, {
    update(cache, { data: { updateATSDecision } }) {
      cache.modify({
        id: cache.identify({ _id: user._id, __typename: 'User' }),
        fields: {
          client: (existing) => ({
            ...existing,
            usesATS: updateATSDecision.usesATS,
            selectedATSProvider: updateATSDecision.selectedATSProvider,
          }),
        },
      });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    },
  });

  const handleSelect = async (usesATS) => {
    await updateDecision({ variables: { usesATS } });
    if (usesATS) {
      navigate('/onboarding/ats-selector');
    } else {
      navigate('/jobs');
    }
  };

  return (
    <Box
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: 'sm',
        mx: 'auto',
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography level="h4" gutterBottom>How do you manage candidates today?</Typography>
        <Typography level="body-sm" color="neutral">
          This helps us set up your hiring workflow in Cavalry
        </Typography>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card
          variant="outlined"
          onClick={() => !loading && handleSelect(true)}
          sx={{
            flex: 1,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: 'primary.400', boxShadow: 'sm' },
          }}
        >
          <IntegrationInstructionsIcon sx={{ fontSize: 36, color: 'primary.400', mb: 1 }} />
          <Typography level="title-md">We use an ATS</Typography>
          <Typography level="body-sm">
            We manage candidates in a system like Greenhouse, Ashby, etc.
          </Typography>
          <Typography level="body-xs" color="primary" sx={{ mt: 1 }}>
            You&apos;ll be able to import candidates automatically
          </Typography>
        </Card>

        <Card
          variant="outlined"
          onClick={() => !loading && handleSelect(false)}
          sx={{
            flex: 1,
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.15s, box-shadow 0.15s',
            '&:hover': { borderColor: 'primary.400', boxShadow: 'sm' },
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 36, color: 'primary.400', mb: 1 }} />
          <Typography level="title-md">We don&apos;t use an ATS</Typography>
          <Typography level="body-sm">
            We manage candidates manually (email, spreadsheets, LinkedIn, etc.)
          </Typography>
          <Typography level="body-xs" color="primary" sx={{ mt: 1 }}>
            You&apos;ll get a simple workflow to invite candidates via link
          </Typography>
        </Card>
      </Stack>

      <Typography level="body-xs" color="neutral" sx={{ textAlign: 'center', mt: 1 }}>
        Not sure? Choose the option that feels closest &mdash; you can change it later
      </Typography>

      {loading && (
        <Button loading variant="plain" color="neutral" sx={{ mx: 'auto' }}>
          Saving&hellip;
        </Button>
      )}
    </Box>
  );
};

export default ATSDecision;
